/** @jsx jsx */
import {
  Button,
  ConditionalWrapper,
  Grid,
  Icon,
  IconButton,
  Modal,
  Spacer,
  Table,
  TextGroupMedium,
  TextGroupSmall,
  useMediaQuery,
} from "@cd2/components"
import { IxRef } from "@cd2/components/build/es6/types/utils"
import { Fields, Form2 } from "@cd2/forms"
import { useForm } from "@cd2/forms/build/es6/Form2"
import { FormState } from "@cd2/forms/build/es6/Form2/FormState"
import { IWebClient } from "@cd2/skeem-client"
import { H4, P, TextBlock } from "@cd2/text"
import { formatDate, IComponentCallback, IComponentProps, pluralize } from "@cd2/tools"
import { css, jsx } from "@emotion/core"
import { add, addBusinessDays } from "date-fns"
import React, { useEffect, useImperativeHandle, useRef, useState } from "react"
import { NewForm2Submit } from "../NewComponents/Buttons"
import { InfoBanner } from "../Pages/AlertBanners"
import {
  IOrderRequest,
  ORDER_REQUEST_ITEM_NEW_ID_PREFIX,
} from "../Pages/OrderRequests/OrderRequestsForm"
import {
  BoxQuantitySwitch,
  useStockedInAnotherBranch,
} from "../Pages/OrderRequests/OrderRequestsStandaloneForm"
import { icons } from "../constants/icons"
import { useProductGroupProductPrice } from "../hooks/productGroupProductPrice"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { useConfirmation } from "../hooks/useConfirmation"
import {
  alternativeProductAttribute,
  ConfirmAlternateProductModal,
  useProductAlternative,
} from "../hooks/useProductAlternative"
import { useSiteAlerts } from "../hooks/useSiteAlerts"
import { ProductSelect } from "../stories/quoteOrders/ItemRowSageProduct"
import { isProductExported } from "../stories/quoteOrders/quoteOrderUtils"
import { IValidationResponse } from "../types/common"
import { IRecord } from "../types/schema"
import type { IProductStock } from "../../server/productLocations/aggregateProductStock"
import { formStateSetErrors } from "../utils/formStateSetErrors"
import { getProductLocationPrioritized } from "../utils/getProductLocations"
import { productDpdFilter } from "../utils/productDpdFilter"
import { IBranchSettings, useSettings } from "../utils/useSettings"
import { currencyNameToSymbol } from "../utils/utils"
import { addressAsArray, AddressDisplayPanel, IAddressRecord } from "./AddressDisplayPanel"
import { AddressFormFields } from "./AddressFormFields"
import { ConfirmCancelButtons } from "./Buttons/ConfirmCancelButtons"
import { useGlobalState } from "./GlobalStateProvider"
import { FloatingBox } from "./Misc/FloatingBox"
import { AddressForm } from "./Pages/CustomerShowPage/AddressForm"
import { IProductForSelect } from "./ProductSelect"
import { QuickToolTipWrapper } from "./QuickToolTipWrapper"
import { QuickToolTip } from "./QuickTooltip"
import { WarehouseSelect } from "./SelectLists/WarehouseSelectList"
import { TextWithNewLines } from "./TextWithNewLines"
import { useAsyncCallback } from "../hooks/useAsyncCallback"
import { ConfirmOnClick } from "./ConfirmOnClick"
import { IProductLocationStockResponseItem, useApi } from "../hooks/useApi"
import {
  combineProductLocationStockData,
  ProductLocationStockDisplaySwitcher,
} from "../apiHooks/productLocations"
import { useOrderRequestDefaultSettings } from "../hooks/useOrderRequestDefaultSettings"
import { skeem } from "../utils/skeem"

export interface IPurchaseOrdersForm2Item {
  key: string
  deleted?: boolean
  onOrderRequest?: boolean
  id?: string
  product?: {
    id: string
    stock_code: string
    body: string
    preferred_supplier_list_price_pennies: number | null
    cost_price_pennies: number | null
    product_locations: IRecord<
      "product_locations",
      "name" | "free_stock" | "warehouse" | "specific_location"
    >[]
    sage_id: string
    bypassed_stock_check?: boolean
  }
  product_location?: { id: string }
  quantity: number | null
  price_pennies: number | null
  body?: string
  commentLine?: boolean
  buildRequired?: boolean
}

export interface IPurchaseOrdersForm2Data {
  id?: string
  supplier?: { id: string; name: string; isLocked?: boolean; currency_name: string } | null
  address?: IComponentCallback<typeof AddressForm, "onSubmit"> & { id?: string | undefined }
  purchase_order_items?: IPurchaseOrdersForm2Item[]
  sourceOrder?: IRecord<"orders">
  carriage_pennies?: number | null
  order_requested_date?: Date | null
  supplierDocumentNumber?: string
  preferred_warehouse?: IRecord<"warehouses", "name">
  hide_tax_on_documents?: boolean
}

/**
 * Set while editing a purchase order that has been exported to Sage. Only some of its fields are
 * sent to Sage when it is updated there (line quantity and price, added and removed lines,
 * requested date, supplier document number), so the others can't be changed.
 */
const PurchaseOrderSageLockContext = React.createContext(false)

export function PurchaseOrderSageLockProvider({
  locked,
  children,
}: {
  locked?: boolean
  children: React.ReactNode
}) {
  return (
    <PurchaseOrderSageLockContext.Provider value={!!locked}>
      {children}
    </PurchaseOrderSageLockContext.Provider>
  )
}

/** Whether the fields Sage isn't updated with are locked, see PurchaseOrderSageLockProvider */
export function usePurchaseOrderSageLock() {
  return React.useContext(PurchaseOrderSageLockContext)
}

export interface IPurchaseOrdersForm2Props {
  /** The purchase order has been exported to Sage, see PurchaseOrderSageLockProvider */
  sageUpdateLocked?: boolean
  xRef?: IxRef<FormState<IPurchaseOrdersForm2Data> | null>
  initialData?: Partial<IPurchaseOrdersForm2Data>
  errors?: IPurchaseOrdersForm2Errors
  noSubmitButtons?: boolean
  rightNode?: React.ReactNode
  isBuildRequired?: (productId: string) => boolean
  toggleBuildRequired?: (productId: string) => void
  hasOrderRequest?: boolean
  defaultWarehouse?: IRecord<"warehouses", "name">

  onSubmit?(data: IPurchaseOrdersForm2Data): void

  onDataChange?(data: IPurchaseOrdersForm2Data): void

  onAddItemToOrderRequest?(item: IPurchaseOrdersForm2Item, product: IProduct): void

  addNonMatchingProductToOrderRequest?(item: IPurchaseOrdersForm2Item): void

  onViewOrderRequestItem?(): void

  onSetShipTo?(data: IAddressRecord): void
}

export interface IPurchaseOrdersForm2Errors {
  supplier?: string
  address?: string
  address_name?: string
  address_line_1?: string
  address_postcode?: string
  purchase_order_items_length?: string
  // Record<item.key, errors>
  purchase_order_items?: Record<string, IPurchaseOrdersForm2ItemErrors>
  body?: string
}

export interface IPurchaseOrdersForm2ItemErrors {
  product?: string
  product_location?: string
  quantity?: string
  price_pennies?: string
  body?: string
}

export { validateForm as validatePurchaseOrdersForm }

function validateForm(
  data: IPurchaseOrdersForm2Data,
  validateOrItems?: { data: IOrderRequest; mustBeOnOrderRequest: boolean },
): IValidationResponse<IPurchaseOrdersForm2Errors> {
  let isValid = true
  const errors: IPurchaseOrdersForm2Errors = {}

  if (!data.supplier) {
    errors.supplier = "Supplier is required"
    isValid = false
  }

  const nonDeletedItems = (data.purchase_order_items ?? []).filter(item => !item.deleted)
  if (nonDeletedItems.length === 0) {
    errors.purchase_order_items_length = "You must have at least 1 item"
    isValid = false
  } else {
    nonDeletedItems.forEach(item => {
      const itemErrors: IPurchaseOrdersForm2ItemErrors = {}

      if (item.deleted || item.onOrderRequest) {
        return
      }

      if (item.commentLine) {
        // Comment line errors
        if (!item.body) {
          itemErrors.body = "must be present"
          isValid = false
        }
      } else {
        // Product line errors
        if (!item.product) {
          itemErrors.product = "must be present"
          isValid = false
        } else if (
          nonDeletedItems.some(
            other => other.key !== item.key && other.product?.id === item.product?.id,
          )
        ) {
          itemErrors.product = "This product appears multiple times"
          isValid = false
        }

        if (!item.product_location) {
          itemErrors.product_location = "must be present"
          isValid = false
        }

        if (!item.quantity || item.quantity < 1) {
          itemErrors.quantity = "must be greater than 0"
          isValid = false
        }

        if (!item.price_pennies) {
          itemErrors.price_pennies = "must be greater than 0"
          isValid = false
        }

        if (validateOrItems?.mustBeOnOrderRequest) {
          const orderRequest = validateOrItems.data
          const matchingItem = orderRequest.items?.find(orItem => orItem.id === item.key)
          if (!matchingItem) {
            itemErrors.product = "This product is not on the order request"
            isValid = false
          }
        }
      }
      if (Object.keys(itemErrors).length > 0) {
        errors.purchase_order_items = errors.purchase_order_items ?? {}
        errors.purchase_order_items[item.key] = itemErrors
      }
    })
  }

  return { isValid, errors }
}

const purchaseOrderProductAttributes = [
  "stock_code",
  "legacy_stock_code",
  "body",
  "cost_price_pennies",
  "preferred_supplier_list_price_pennies",
  "line_item_description",
  "sage_id",
  "active",
  "is_consignment",
  {
    name: "product_locations",
    attributes: ["name", "free_stock", "warehouse", "specific_location"],
  },
  alternativeProductAttribute,
]

/**
 * The rules for putting a product on a purchase order line, shared by the classic and new design
 * forms: offer the product's alternative, pick its location (preferring the default warehouse)
 * and price it at the product group's cost price where there is one.
 */
export function useResolvePurchaseOrderProduct(
  defaultWarehouse: IRecord<"warehouses", "name"> | null,
) {
  const skeem = window.skeem
  const { currentUser } = useGlobalState()
  const { defaultStockLocation, warehouses, defaultIntercoWarehouse } = useSettings()
  const checkProductForAlternative = useProductAlternative()
  const productGroupPrice = useProductGroupProductPrice()
  const [confirmAltProductNode, confirmAltProduct] = useConfirmation<any, { id: string }>(
    ({ onResolve, data }) => <ConfirmAlternateProductModal data={data} onResolve={onResolve} />,
  )

  function getProductLocationForProduct(product: {
    product_locations: IRecord<
      "product_locations",
      "name" | "warehouse" | "free_stock" | "specific_location"
    >[]
    is_consignment?: boolean
  }) {
    return getProductLocationPrioritized(
      product.product_locations,
      {
        defaultStockLocation: currentUser?.defaultWarehouse?.name ?? defaultStockLocation,
        branch: currentUser.branch,
        warehouses: warehouses,
        defaultIntercoWarehouse: defaultIntercoWarehouse,
      },
      { isConsignment: product.is_consignment, isInterco: true, prioritize: defaultWarehouse },
    )
  }

  /** Loads a product with everything a purchase order line needs, e.g. from the shortfall list */
  async function loadProduct(productId: string) {
    return skeem.fetch2<IProductForSelect>({
      first: {
        name: "products",
        attributes: [
          "stock_code",
          "legacy_stock_code",
          "body",
          "cost_price_pennies",
          "preferred_supplier_list_price_pennies",
          "line_item_description",
          "sage_id",
          "active",
          "is_consignment",
          {
            name: "product_locations",
            attributes: ["name", "free_stock", "warehouse"],
          },
          alternativeProductAttribute,
        ],
        matches: { id: productId },
      },
    })
  }

  /** The line values for a chosen product, after offering its alternative */
  async function resolveProduct(chosen: IProductForSelect) {
    let product = chosen
    const alt = await checkProductForAlternative(product.id)
    if (alt.alternative) {
      const selected = await confirmAltProduct(alt)
      product = await skeem.fetch2<IProductForSelect>({
        first: {
          name: "products",
          attributes: purchaseOrderProductAttributes as any,
          filter: { matches: { id: selected.id } },
        },
      })
    }
    const location = getProductLocationForProduct(product)
    const groupPrice = await productGroupPrice(product.id)
    return {
      product,
      product_location: location,
      price_pennies: groupPrice?.cost_price_pennies ?? product.cost_price_pennies,
    }
  }

  return { node: confirmAltProductNode, loadProduct, resolveProduct }
}

/**
 * Shows the product's and its product group's memos, if they have any, after a product is put
 * on a line.
 */
export function useProductMemoAlerts() {
  const skeem = window.skeem
  const [memoNode, alertMemo] = useConfirmation<
    { memo: string; name: string; type: "product" | "product group" },
    void
  >(({ onResolve, data }) => (
    <Modal
      isOpen={true}
      onRequestClose={() => onResolve()}
      css={css`
        max-width: calc(100% - 16px);
        overflow: initial;
        text-align: left;
      `}
      render={() => (
        <React.Fragment>
          <TextGroupMedium
            heading={`Memo for ${data.name}`}
            body={`This memo has been added to the ${data.type}'s record. Change on the ${data.type} edit page.`}
            theme={{ heading: { size: "small" } }}
          />
          <Spacer />
          <div
            css={css`
              border-radius: 8px;
              background-color: #f5f5f5;
              padding: 16px;
            `}
          >
            <TextWithNewLines text={data.memo} />
          </div>
          <Spacer />
          <div>
            <Button onClick={() => onResolve()} buttonColor="white">
              Okay
            </Button>
          </div>
        </React.Fragment>
      )}
    />
  ))

  async function showProductMemos(product: { id: string; body: string }) {
    const memoData = await skeem.fetch2<
      IRecord<"products", "memo" | "body"> & {
        product_group?: IRecord<"product_groups", "memo" | "code">
      }
    >({
      first: {
        name: "products",
        attributes: ["memo", "body", { name: "product_group", attributes: ["memo", "code"] }],
        matches: { id: product.id },
      },
    })
    if (memoData.product_group?.memo) {
      await alertMemo({
        name: `${memoData.body}'s product group ${memoData.product_group?.code}`,
        memo: memoData.product_group?.memo,
        type: "product group",
      })
    }
    if (memoData.memo) {
      await alertMemo({ name: product.body, memo: memoData.memo, type: "product" })
    }
  }

  return { node: memoNode, showProductMemos }
}

/** A new, empty product line */
export function newPurchaseOrderItem(): IPurchaseOrdersForm2Item {
  return {
    key: `${ORDER_REQUEST_ITEM_NEW_ID_PREFIX}_${Math.random()}`,
    quantity: 1,
  } as IPurchaseOrdersForm2Item
}

/** A new, empty comment line */
export function newPurchaseOrderCommentItem(hasOrderRequest?: boolean): IPurchaseOrdersForm2Item {
  return {
    key: `${ORDER_REQUEST_ITEM_NEW_ID_PREFIX}_${Math.random()}`,
    quantity: 1,
    body: "",
    commentLine: true,
    onOrderRequest: !!hasOrderRequest,
  } as IPurchaseOrdersForm2Item
}

/** Points every line at the given warehouse's location for its product, where it has one */
export function applyWarehouseToItems(
  items: IPurchaseOrdersForm2Item[],
  warehouse: IRecord<"warehouses", "name">,
) {
  return items.map(item => {
    if (item.deleted) {
      return item
    }
    return {
      ...item,
      product_location: item.product?.product_locations.find(
        x => x.warehouse?.id === warehouse?.id,
      ),
    }
  })
}

/** Starting values for the ship to address field */
export const emptyPurchaseOrderAddress = {
  id: "",
  city: "",
  contact: "",
  county: "",
  email: "",
  line_1: "",
  line_2: "",
  line_3: "",
  line_4: "",
  name: "",
  phone: "",
  postcode: "",
}

/** Whether the address field holds an address (it starts as an object of empty strings) */
export function hasPurchaseOrderAddress(address?: Partial<IAddressRecord> | null) {
  return Object.values(address ?? {}).some(v => !!v)
}

/**
 * ⌘/Ctrl+S adds a product line and ⌘/Ctrl+G a comment line (the same keys as the Quote / Order
 * forms). Returns the modifier key's symbol for button hints.
 */
export function useLineShortcuts({
  enabled,
  onAddItem,
  onAddComment,
}: {
  enabled: boolean
  onAddItem: () => void
  onAddComment: () => void
}) {
  const isMac =
    typeof navigator !== "undefined" && navigator.platform.toUpperCase().indexOf("MAC") >= 0
  const modKey = isMac ? "⌘" : "Ctrl"

  // Keep the latest handlers in a ref so the key listener never sees stale items
  const shortcutHandlersRef = useRef({ onAddItem, onAddComment, enabled })
  shortcutHandlersRef.current = { onAddItem, onAddComment, enabled }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey
      const key = e.key.toLowerCase()
      if (!cmdOrCtrl || (key !== "s" && key !== "g")) return

      e.preventDefault()
      const handlers = shortcutHandlersRef.current
      if (!handlers.enabled) return

      if (key === "s") {
        handlers.onAddItem()
      } else {
        handlers.onAddComment()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isMac])

  return { modKey }
}

/** Validates the form and calls onSubmit when it passes, as both designs of the form do */
export function submitPurchaseOrderForm(
  data: IPurchaseOrdersForm2Data,
  formState: FormState,
  {
    onSubmit,
    onInvalid,
    alerts,
  }: {
    onSubmit?: (data: IPurchaseOrdersForm2Data) => void
    /** Told about every problem when validation fails, e.g. to list them in a banner */
    onInvalid?: (errors: IPurchaseOrdersForm2Errors) => void
    alerts: ReturnType<typeof useSiteAlerts>
  },
) {
  const validationResult = validateForm(data)
  console.log("Purchase order validation response", validationResult)
  formStateSetErrors(formState, validationResult.errors)
  if (validationResult.isValid) {
    return onSubmit?.(data)
  }
  onInvalid?.(validationResult.errors)
  if (validationResult.errors.purchase_order_items_length) {
    alerts.genericError("Must have at least 1 item")
  }
}

export function PurchaseOrdersForm2({
  xRef,
  initialData,
  onSubmit,
  rightNode,
  onDataChange,
  onAddItemToOrderRequest,
  addNonMatchingProductToOrderRequest,
  onViewOrderRequestItem,
  errors,
  noSubmitButtons,
  onSetShipTo,
  isBuildRequired,
  toggleBuildRequired,
  hasOrderRequest,
  defaultWarehouse: initialDefaultWarehouse,
  sageUpdateLocked,
}: IPurchaseOrdersForm2Props) {
  const skeem = window.skeem
  const [defaultWarehouse, setDefaultWarehouse] = useState<IRecord<"warehouses", "name"> | null>(
    initialDefaultWarehouse ?? null,
  )
  const isMobile = useMediaQuery({ maxWidth: 640 })
  const alerts = useSiteAlerts()

  const formRef = useRef<FormState>(null)

  const [addressModalOpen, setAddressModalOpen] = useState(false)
  const { currentUser } = useGlobalState()
  const {
    defaultStockLocation,
    defaultIntercoWarehouse,
    warehouses,
    canViewPoShortfallItemsPanel,
    canBulkAssignWarehouse,
    _loaded: settingsLoaded,
  } = useSettings()

  const resolvePurchaseOrderProduct = useResolvePurchaseOrderProduct(defaultWarehouse)

  useImperativeHandle(xRef, () => formRef.current, [formRef.current])

  useEffect(() => {
    if (!formRef.current) {
      return
    }
    formStateSetErrors(formRef.current, errors || {})
  }, [formRef.current, JSON.stringify(errors)])

  function handleSubmit(data: IPurchaseOrdersForm2Data, formState: FormState) {
    return submitPurchaseOrderForm(data, formState, { onSubmit, alerts })
  }

  // This use effect passes up the form ref immediately if initial data is present
  // this is due to the create PO modal that can build a valid payload but you cant save until
  // you've trigged on change at least once
  useEffect(() => {
    if (onDataChange && initialData) {
      onDataChange(initialData as IPurchaseOrdersForm2Data)
    }
  }, [])

  function applyDefaultWarehouse() {
    if (!defaultWarehouse) {
      return
    }
    const itemField = formRef.current?.getField("purchase_order_items", {
      defaultValue: [],
    })
    const items = itemField?.getValue() as IPurchaseOrdersForm2Item[]
    if (!items) {
      return
    }
    itemField?.setValue(applyWarehouseToItems(items, defaultWarehouse))
  }

  return (
    <PurchaseOrderSageLockProvider locked={sageUpdateLocked}>
      {resolvePurchaseOrderProduct.node}
      {/*<InfoBanner*/}
      {/*  message={*/}
      {/*    <React.Fragment>*/}
      {/*      <H4>*/}
      {/*        If you wish to order this from another branch, please click here to create an order*/}
      {/*        request.*/}
      {/*      </H4>{" "}*/}
      {/*      <Spacer />*/}
      {/*      <BaseButton {...buttonConfigs.create} onClick={handleChangeToOrderRequest}>*/}
      {/*        New order request*/}
      {/*      </BaseButton>*/}
      {/*    </React.Fragment>*/}
      {/*  }*/}
      {/*/>*/}
      {/*<Spacer />*/}
      <Form2.Form
        xRef={formRef}
        initialData={{
          ...initialData,
          order_requested_date: initialData?.order_requested_date ?? add(new Date(), { weeks: 1 }),
          preferred_warehouse: defaultWarehouse,
        }}
        onSubmit={handleSubmit}
        onChange={data => {
          onDataChange?.(data)
        }}
      >
        <Grid
          columns={
            isMobile
              ? ["auto"]
              : [
                  { min: 300, max: 500 },
                  { min: 250, max: 2570 },
                ]
          }
          gap={16}
        >
          <Form2.WithForm
            subscribe={{ dataChange: true }}
            render={(form: FormState<IPurchaseOrdersForm2Data>) => {
              const supplierField = form.getField("supplier")
              return (
                <FloatingBox
                  header={<H4>1. Select Supplier</H4>}
                  css={css`
                    max-width: 500px;
                  `}
                >
                  <SupplierSelectAsync
                    supplierId={supplierField.getValue()?.id ?? null}
                    error={supplierField.getError()}
                    disabled={sageUpdateLocked}
                    onChange={supplier => {
                      supplierField.setValue(supplier)
                      if (supplier.defaultLeadTimeDays) {
                        form
                          .getField("order_requested_date")
                          .setValue(addBusinessDays(new Date(), supplier.defaultLeadTimeDays))
                      }
                    }}
                  />
                </FloatingBox>
              )
            }}
          />
          {rightNode}
          <FloatingBox
            header={<H4>Select a warehouse for default product location (optional)</H4>}
            css={css`
              max-width: 500px;
            `}
            footer={
              <P size="small">
                When set, the product location field on items will prefer this warehouse if it is
                available.
              </P>
            }
          >
            <Grid gap={8}>
              {settingsLoaded && (
                <React.Fragment>
                  <Form2.WithField
                    field="preferred_warehouse"
                    subscribe={true}
                    render={prefWarehouseField => (
                      <WarehouseSelect
                        key={warehouses
                          .map(x => x.id)
                          .sort((a, b) => a.localeCompare(b))
                          .join("_")}
                        onChange={(_, warehouse) => {
                          setDefaultWarehouse(warehouse)
                          prefWarehouseField.setValue(warehouse)
                        }}
                        value={defaultWarehouse?.id}
                        branchId={currentUser.branch?.id ?? undefined}
                        filter={
                          warehouses && warehouses.length > 0
                            ? { matches: { id: { or: warehouses.map(x => x.id) } } }
                            : undefined
                        }
                        isClearable
                      />
                    )}
                  />
                  {canBulkAssignWarehouse && !sageUpdateLocked && (
                    <div>
                      <ConfirmOnClick
                        onConfirm={applyDefaultWarehouse}
                        heading="Are you sure you want to apply this warehouse?"
                        body={
                          <P>
                            All items in this purchase order will attempt to update their location
                            to the warehouse specified.
                          </P>
                        }
                      >
                        <Button disabled={!defaultWarehouse?.id}>Apply to all items</Button>
                      </ConfirmOnClick>
                    </div>
                  )}
                </React.Fragment>
              )}
            </Grid>
          </FloatingBox>
        </Grid>
        <Form2.WithForm
          subscribe={{ dataChange: true, submitting: true }}
          render={(form: FormState<IPurchaseOrdersForm2Data>) => {
            const supplier = form.getField("supplier").getValue()
            const itemsField = form.getField("purchase_order_items", { defaultValue: [] })
            if (!supplier) {
              return null
            }
            return (
              <div>
                {supplier.isLocked && (
                  <InfoBanner
                    message={
                      <React.Fragment>
                        <P>This supplier has been locked.</P>
                        <P>Products from this supplier must be ordered through central.</P>
                        <P>
                          Either change supplier or add all items to an order request to save this
                          purchase order.
                        </P>
                      </React.Fragment>
                    }
                    compact
                  />
                )}
                <Form2.WithField
                  field="purchase_order_items_length"
                  render={field =>
                    field.getError() ? <P color="red">{field.getError()}</P> : null
                  }
                  subscribe
                />
                {canViewPoShortfallItemsPanel && (
                  <SupplierShortfallList
                    supplierId={supplier.id}
                    onAddProduct={async (productId, qty) => {
                      const product = await resolvePurchaseOrderProduct.loadProduct(productId)
                      const resolved = await resolvePurchaseOrderProduct.resolveProduct(product)
                      const newItem = {
                        key: `${ORDER_REQUEST_ITEM_NEW_ID_PREFIX}_${Math.random()}`,
                        quantity: qty,
                        ...resolved,
                      }
                      console.log("newItem", newItem)
                      itemsField.setValue([...(itemsField.getValue() ?? []), newItem])
                    }}
                    filterProductIds={itemsField
                      .getValue()
                      ?.filter(x => !x.deleted && x.product?.id)
                      .map(x => x.product!.id)}
                  />
                )}
                <PurchaseOrderItems
                  supplierId={supplier.id}
                  onAddItemToOrderRequest={onAddItemToOrderRequest}
                  addNonMatchingProductToOrderRequest={addNonMatchingProductToOrderRequest}
                  onViewOrderRequestItem={onViewOrderRequestItem}
                  isBuildRequired={isBuildRequired}
                  toggleBuildRequired={toggleBuildRequired}
                  hasOrderRequest={hasOrderRequest}
                  defaultWarehouse={defaultWarehouse}
                />
                <Spacer />
                <FloatingBox header={<H4>3. Ship to address</H4>}>
                  <Form2.WithField<Partial<IAddressRecord>>
                    subscribe={true}
                    defaultValue={emptyPurchaseOrderAddress}
                    field="address"
                    render={addressField => (
                      <React.Fragment>
                        {addressField.getError() && <P color="red">{addressField.getError()}</P>}
                        <Grid columns={["auto", "max-content"]} gap={8}>
                          {Object.values(addressField.getValue() ?? {}).some(v => !!v) && (
                            <AddressDisplayPanel address={addressField.getValue()} />
                          )}
                          <div>
                            <Button onClick={() => setAddressModalOpen(true)}>
                              {Object.values(addressField.getValue() ?? {}).some(v => !!v)
                                ? "Edit"
                                : "Add"}{" "}
                              Address
                            </Button>
                          </div>
                        </Grid>
                        <Modal
                          isOpen={addressModalOpen}
                          render={() => (
                            <Form2.Form
                              initialData={addressField.getValue() ?? {}}
                              onSubmit={data => {
                                addressField.setValue(data)
                                onSetShipTo?.(data)
                                setAddressModalOpen(false)
                              }}
                            >
                              <StoredAddressSelect />
                              <AddressFormFields />
                              <Form2.WithForm
                                render={form => (
                                  <ConfirmCancelButtons
                                    onConfirm={() => {
                                      form.submit()
                                    }}
                                    onCancel={() => setAddressModalOpen(false)}
                                  />
                                )}
                              />
                            </Form2.Form>
                          )}
                        />
                      </React.Fragment>
                    )}
                  />
                </FloatingBox>
                <Spacer />
                <FloatingBox header={<H4>4. Details</H4>}>
                  <Grid gap={8}>
                    <Form2.WithField<number | null>
                      field="carriage_pennies"
                      render={field => (
                        <PoPriceField
                          label="Carriage"
                          placeholder="0.00"
                          value={field.getValue()}
                          onChange={field.setValue}
                          inPennies
                        />
                      )}
                    />
                    <Form2.Date label="Order requested on" field="order_requested_date" />
                    <Form2.Text label="Supplier Document Number" field="supplierDocumentNumber" />
                    <Form2.Checkbox field="hide_tax_on_documents" label="Hide VAT on PDF" />
                  </Grid>
                </FloatingBox>
                <Spacer />
                {!noSubmitButtons && <NewForm2Submit />}
              </div>
            )
          }}
        />
      </Form2.Form>
    </PurchaseOrderSageLockProvider>
  )
}

export function PoPriceField(props: IComponentProps<typeof Fields.Price>) {
  return (
    <Form2.WithField<ISupplierRecord>
      field="supplier"
      render={supplier => (
        <Fields.Price
          {...props}
          prefix={currencyNameToSymbol(supplier.getValue()?.currency_name ?? "") || "£"}
        />
      )}
    />
  )
}

export function StoredAddressSelect({ skeemFilter }: { skeemFilter?: any }) {
  const form = useForm()
  const skeem = window.skeem
  const addressAttributes = [
    "label",
    "name",
    "email",
    "phone",
    "line_1",
    "line_2",
    "line_3",
    "line_4",
    "city",
    "county",
    "country",
    "postcode",
    "contact",
  ] as any

  async function handleAddressLookup(value: string) {
    const addresses = await skeem.fetch<
      IRecord<
        "addresses",
        | "label"
        | "name"
        | "email"
        | "phone"
        | "line_1"
        | "line_2"
        | "line_3"
        | "line_4"
        | "city"
        | "county"
        | "country"
        | "postcode"
        | "contact"
      >
    >({
      addresses: {
        attributes: addressAttributes,
        filter: {
          and: [
            { matches: { stored: true } },
            {
              or: [
                { like: [{ attr: "label" }, { value }] },
                { like: [{ attr: "name" }, { value }] },
                { like: [{ attr: "email" }, { value }] },
                { like: [{ attr: "contact" }, { value }] },
                { like: [{ attr: "postcode" }, { value }] },
              ],
            },
            skeemFilter,
          ].filter(Boolean),
        },
        sort: { by: "label", direction: "ASC" },
        paginate: { page: 1, size: 30 },
      },
    })

    return addresses.map(data => ({ label: addressAsArray(data).join(", "), value: data }))
  }

  function handleAddressSelect(storedAddress: { [attr: string]: string }) {
    const safeAddress: { [attr: string]: string } = {}
    for (const attr of addressAttributes) {
      safeAddress[attr] = storedAddress[attr]
    }
    form.setData(safeAddress)
  }

  return (
    <Fields.Select
      label="Select from stored addresses"
      options={[]}
      loadOptions={handleAddressLookup as any}
      onChange={handleAddressSelect as any}
      value={""}
      cacheOptions
    />
  )
}

export type ISupplierRecord = IRecord<"suppliers", "name" | "currency_name"> & {
  addresses: IAddressRecord[]
  products: IRecord<"products">[]
  isLocked: boolean
  memo?: string
  defaultLeadTimeDays?: number
}

interface ISupplierSelectProps {
  supplierId: string | null
  onChange: (supplier: ISupplierRecord) => void
  disabled?: boolean
  error?: string
  /** Leave off the field's "Supplier" label, e.g. when it's in a card titled Supplier */
  hideLabel?: boolean
}

type ISupplier = IRecord<"suppliers", "name" | "account_reference"> & {
  products: IProduct[]
  isDisabled: boolean
  isLocked: boolean
  memo?: string
}

export function SupplierSelectAsync({
  supplierId,
  onChange,
  disabled,
  error,
  hideLabel,
}: ISupplierSelectProps) {
  const skeem = window.skeem
  const { currentUser } = useGlobalState()
  const form: FormState<IPurchaseOrdersForm2Data> = Form2.useForm({
    subscribe: { dataChange: true, submitting: true },
  })
  const supplierField = form.getField("supplier")

  async function fetchSuppliers(query: string) {
    const records = await skeem.fetch<ISupplier>({
      suppliers: {
        attributes: [
          "name",
          "account_reference",
          "products",
          "currency_name",
          {
            as: "memo",
            value: {
              path: [
                {
                  name: "supplier_master",
                  filter: {
                    exists: {
                      path: [
                        "memoBranches",
                        { name: "users", filter: { matches: { id: { session: true } } } },
                      ],
                    },
                  },
                },
                "memo",
              ],
            },
          },
          {
            name: "addresses",
            attributes: [
              "name",
              "contact",
              "line_1",
              "line_2",
              "line_3",
              "line_4",
              "city",
              "county",
              "country",
              "postcode",
              "phone",
              "email",
            ],
            sort: { by: "created_at", direction: "DESC" },
            filter: { matches: { deleted: false } },
          },
          {
            as: "defaultLeadTimeDays",
            value: { path: ["supplier_master", "defaultLeadTimeDays"] },
          },
          {
            as: "isLocked",
            value: {
              path: [
                {
                  name: "supplier_master",
                  filter: {
                    exists: {
                      path: [
                        "poLockBranches",
                        { name: "users", filter: { matches: { id: { session: true } } } },
                      ],
                    },
                  },
                },
                "purchase_order_locked",
              ],
            },
          },
        ],
        paginate: { size: 30 },
        filter: {
          and: [
            { matches: { branch: currentUser.branch.id } },
            {
              or: [
                { matches: { name: { like: query } } },
                { matches: { account_reference: { like: query } } },
              ],
            },
          ],
        },
        sort: { by: "name", direction: "ASC" },
      },
    })
    return records
  }

  function handleSupplierChange(supplier: ISupplierRecord) {
    supplierField.setValue(supplier)
    if (supplier.memo) {
      alertSupplierMemo(supplier)
    }
    onChange(supplier)
  }

  const [supplierMemoNode, alertSupplierMemo] = useConfirmation<
    { memo?: string; name: string },
    void
  >(({ onResolve, data }) => (
    <Modal
      isOpen={true}
      onRequestClose={() => onResolve()}
      css={css`
        max-width: calc(100% - 16px);
        overflow: initial;
        text-align: left;
      `}
      render={() => (
        <React.Fragment>
          <TextGroupMedium
            heading={`Memo for ${data.name}`}
            body="This memo has been added to the suppliers's record. Change on the supplier masters edit page."
            theme={{ heading: { size: "small" } }}
          />
          <Spacer />
          <div
            css={css`
              border-radius: 8px;
              background-color: #f5f5f5;
              padding: 16px;
            `}
          >
            <TextWithNewLines text={data.memo ?? ""} />
          </div>
          <Spacer />
          <div>
            <Button onClick={() => onResolve()} buttonColor="white">
              Okay
            </Button>
          </div>
        </React.Fragment>
      )}
    />
  ))

  const accountRefCss = css`
    font-size: 12px;
    font-weight: 600;
    color: #999;
  `

  return (
    <React.Fragment>
      {supplierMemoNode}
      <Fields.AsyncSelect
        key={String(supplierId)}
        label={hideLabel ? undefined : "Supplier"}
        value={supplierField.getValue()}
        onChange={handleSupplierChange as any}
        loadOptions={fetchSuppliers}
        getOptionLabel={opt => opt.name}
        getOptionValue={opt => opt.id}
        error={error}
        disabled={disabled}
        components={{
          SingleValue: props => {
            const { innerProps, getStyles } = props
            const supplier = props.data as ISupplier

            return (
              <div
                {...innerProps}
                style={{
                  ...getStyles("singleValue", props),
                }}
              >
                <div>{supplier?.name}</div>
                <div css={accountRefCss}>{supplier?.account_reference}</div>
              </div>
            )
          },
          Option: props => {
            const { innerProps, innerRef, getStyles } = props
            const supplier = props.data as ISupplier

            return (
              <div
                ref={innerRef}
                {...innerProps}
                style={{
                  ...getStyles("option", props),
                }}
              >
                <div>{supplier?.name}</div>
                <div css={accountRefCss}>{supplier?.account_reference}</div>
              </div>
            )
          },
        }}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length === 0 ? "Type to search..." : "No results"
        }
        cacheOptions
      />
    </React.Fragment>
  )
}

interface IPurchaseOrderItemsProps {
  supplierId: string | null
  isBuildRequired?: (productId: string) => boolean
  toggleBuildRequired?: (productId: string) => void
  hasOrderRequest?: boolean
  defaultWarehouse: IRecord<"warehouses", "name"> | null

  onAddItemToOrderRequest?(item: IPurchaseOrdersForm2Item, product: IProduct): void

  addNonMatchingProductToOrderRequest?(item: IPurchaseOrdersForm2Item): void

  onViewOrderRequestItem?(): void
}

function PurchaseOrderItems({
  supplierId,
  onAddItemToOrderRequest,
  onViewOrderRequestItem,
  addNonMatchingProductToOrderRequest,
  isBuildRequired,
  toggleBuildRequired,
  hasOrderRequest,
  defaultWarehouse,
}: IPurchaseOrderItemsProps) {
  const { currentUser } = useGlobalState()

  const form: FormState<IPurchaseOrdersForm2Data> = Form2.useForm({
    subscribe: { dataChange: true, submitting: true },
  })
  const itemsField = form.getField("purchase_order_items", { defaultValue: [] })

  function getError(item: IPurchaseOrdersForm2Item, field: keyof IPurchaseOrdersForm2ItemErrors) {
    const key = item.id || item.key || ""
    const itemErrors: IPurchaseOrdersForm2Errors["purchase_order_items"] = itemsField.getError()

    if (!itemErrors) {
      return ""
    } else {
      return itemErrors[key]?.[field] ?? ""
    }
  }

  function handleAddItem() {
    itemsField.setValue([...(itemsField.getValue() ?? []), newPurchaseOrderItem()])
  }

  function handleCommentItem() {
    itemsField.setValue([
      ...(itemsField.getValue() ?? []),
      newPurchaseOrderCommentItem(hasOrderRequest),
    ])
  }

  const { modKey } = useLineShortcuts({
    enabled: !!supplierId,
    onAddItem: handleAddItem,
    onAddComment: handleCommentItem,
  })

  function handleUpdateItem(
    item: IPurchaseOrdersForm2Item,
    changes: Partial<IPurchaseOrdersForm2Item>,
  ) {
    const key = item.id || item.key || ""
    itemsField.setValue(
      (itemsField.getValue() ?? []).map(other =>
        (other.id || other.key) === key ? { ...other, ...changes } : other,
      ),
    )
  }

  function handleAddItemToOrderRequest(item: IPurchaseOrdersForm2Item, product: IProduct) {
    if (!onAddItemToOrderRequest) {
      return
    }
    onAddItemToOrderRequest(item, product)
    handleUpdateItem(item, { onOrderRequest: true })
  }

  function handleAddNonMatchingProductToOrderRequest(item: IPurchaseOrdersForm2Item) {
    if (!addNonMatchingProductToOrderRequest) {
      return
    }
    addNonMatchingProductToOrderRequest(item)
    handleUpdateItem(item, { onOrderRequest: true })
  }

  function getColumns() {
    // Default columns
    const base = [{ min: 200, max: "auto" }, 200, 80, 120, 120, "max-content"]
    // Add column for location
    return base
  }

  function FormHeader() {
    // Default rows
    const base = [
      <span key="product">
        Product{""}
        <span
          css={css`
            color: #ff4040;
          `}
        >
          *
        </span>
      </span>,
      <span key="quantity">
        Qty{" "}
        <span
          css={css`
            color: #ff4040;
          `}
        >
          *
        </span>
      </span>,
      <span key="price">
        Price{""}
        <span
          css={css`
            color: #ff4040;
          `}
        >
          *
        </span>
      </span>,
      <div key="stock">Stock Lvls</div>,
      <div key="button" />,
    ]
    // Push in the location row after the product
    base.splice(
      1,
      0,
      <span>
        Location{""}
        <span
          css={css`
            color: #ff4040;
          `}
        >
          *
        </span>
      </span>,
    )

    return <React.Fragment>{base}</React.Fragment>
  }

  return (
    <FloatingBox
      header={<H4>2. Products</H4>}
      footer={
        <Grid columns={["max-content", "max-content"]} gap={8}>
          <Button
            leftIcon={icons.plus}
            buttonColor="white"
            disabled={!supplierId}
            onClick={handleAddItem}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <span>Add product to Purchase Order</span>
              <span
                css={css`
                  font-size: 0.75em;
                  opacity: 0.8;
                `}
              >
                {modKey}+S
              </span>
            </div>
          </Button>
          <Button
            leftIcon={icons.text}
            buttonColor="white"
            disabled={!supplierId}
            onClick={handleCommentItem}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <span>Add comment line</span>
              <span
                css={css`
                  font-size: 0.75em;
                  opacity: 0.8;
                `}
              >
                {modKey}+G
              </span>
            </div>
          </Button>
        </Grid>
      }
    >
      <Grid columns={getColumns() as any} gap={16}>
        <FormHeader />

        <FormItemRows
          items={itemsField.getValue()?.filter(el => el.id || !el.deleted) ?? []}
          getColumns={getColumns}
          getError={getError}
          handleAddItemToOrderRequest={handleAddItemToOrderRequest}
          handleUpdateItem={handleUpdateItem}
          onAddItemToOrderRequest={onAddItemToOrderRequest}
          onViewOrderRequestItem={onViewOrderRequestItem}
          addNonMatchingProductToOrderRequest={handleAddNonMatchingProductToOrderRequest}
          supplierId={supplierId}
          isBuildRequired={isBuildRequired}
          toggleBuildRequired={toggleBuildRequired}
          hasOrderRequest={hasOrderRequest}
          defaultWarehouse={defaultWarehouse}
        />
      </Grid>
    </FloatingBox>
  )
}

interface IPassThroughProps {
  getError: (item: IPurchaseOrdersForm2Item, field: keyof IPurchaseOrdersForm2ItemErrors) => any
  handleUpdateItem: (
    item: IPurchaseOrdersForm2Item,
    changes: Partial<IPurchaseOrdersForm2Item>,
  ) => void
  handleAddItemToOrderRequest: (item: IPurchaseOrdersForm2Item, product: IProduct) => void
  getColumns: () => any[]
  isBuildRequired?: (productId: string) => boolean
  toggleBuildRequired?: (productId: string) => void
  hasOrderRequest?: boolean
  defaultWarehouse: IRecord<"warehouses", "name"> | null

  onViewOrderRequestItem?(): void

  onAddItemToOrderRequest?(item: IPurchaseOrdersForm2Item, product: IProduct): void

  addNonMatchingProductToOrderRequest?(item: IPurchaseOrdersForm2Item): void
}

function FormItemRows(
  props: {
    items: IPurchaseOrdersForm2Item[]
    supplierId: string | null
  } & IPassThroughProps,
) {
  return (
    <React.Fragment>
      {props.items.map(item => (
        <FormRow key={item.id || item.key} {...props} item={item} />
      ))}
    </React.Fragment>
  )
}

function FormRow({
  item,
  getError,
  handleUpdateItem,
  onViewOrderRequestItem,
  handleAddItemToOrderRequest,
  onAddItemToOrderRequest,
  getColumns,
  addNonMatchingProductToOrderRequest,
  supplierId,
  isBuildRequired,
  toggleBuildRequired,
  hasOrderRequest,
  defaultWarehouse,
}: {
  item: IPurchaseOrdersForm2Item
  supplierId: string | null
} & IPassThroughProps) {
  const { isFortusSubBranch, warehouses } = useSettings()
  // The product and location of a line already on the order in Sage can't be changed, as only its
  // quantity and price are sent to Sage. A line can be removed and another added.
  const isSageLockedLine = usePurchaseOrderSageLock() && !!item.id
  const resolvePurchaseOrderProduct = useResolvePurchaseOrderProduct(defaultWarehouse)
  const productMemoAlerts = useProductMemoAlerts()
  const itemsField = Form2.useField<IPurchaseOrdersForm2Item[]>("purchase_order_items", {
    subscribe: true,
  })
  const inputHeight = 40

  const currentProductIds =
    itemsField
      .getValue()
      ?.filter(v => !v.deleted)
      ?.map(item => item.product?.id)
      .filter(Boolean) ?? []
  // Pulled this out into its own thing to separate it from the rest of the form, bit easier to read

  if (item.commentLine) {
    const columnData = getColumns()
    const count = columnData.length - 1
    return (
      <React.Fragment>
        <Grid.Area column={[1, count]}>
          {supplierId && (
            <Fields.TextArea
              onChange={value => handleUpdateItem(item, { body: value })}
              value={item.body ?? ""}
              disabled={item.deleted}
              error={getError(item, "body")}
            />
          )}
          <Spacer size={4} />
          {supplierId && hasOrderRequest && (
            <Fields.Checkbox
              label="Include on Order request"
              onChange={value => handleUpdateItem(item, { onOrderRequest: value })}
              value={!!item.onOrderRequest}
              disabled={item.deleted}
            />
          )}
        </Grid.Area>
        <div>
          <IconButton
            buttonColor="white"
            color="black"
            onClick={() => handleUpdateItem(item, { deleted: !item.deleted })}
            icon={item.deleted ? icons.undo : icons.x}
            square
          />
        </div>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment key={item.id || item.key}>
      <div>
        {resolvePurchaseOrderProduct.node}
        {productMemoAlerts.node}
        {supplierId && (
          <div>
            <ProductSelect
              filter={
                currentProductIds && currentProductIds.length > 0
                  ? {
                      and: [
                        {
                          not: {
                            matches: { id: currentProductIds },
                          },
                        },
                      ],
                    }
                  : undefined
              }
              value={item.product}
              error={getError(item, "product")}
              onChange={async value => {
                if (value) {
                  const updateData = await resolvePurchaseOrderProduct.resolveProduct(value)
                  handleUpdateItem(item, updateData)
                  await productMemoAlerts.showProductMemos(updateData.product)
                }
              }}
              disabled={item.deleted || item.onOrderRequest || isSageLockedLine}
              preventSelectUnexported
            />
            <BoxQuantitySwitch
              productId={item.product?.id}
              onChange={quantity => handleUpdateItem(item, { quantity })}
              itemQuantity={item.quantity}
            />
          </div>
        )}
        {item.onOrderRequest && (
          <React.Fragment>
            <Spacer />
            <Fields.Checkbox
              label="Build req"
              onChange={() => (item.product ? toggleBuildRequired?.(item.product.id) : undefined)}
              value={(!!item.product && isBuildRequired?.(item.product.id)) ?? false}
            />
          </React.Fragment>
        )}
      </div>

      <div>
        <Fields.Select
          options={
            item.product?.product_locations
              .filter(
                location =>
                  warehouses.length === 0 || warehouses.find(w => w.id === location.warehouse?.id),
              )
              .map(location => ({
                value: location.id,
                label: (
                  <div>
                    {location.name}
                    {location.specific_location ? (
                      <span
                        css={css`
                          font-size: 12px;
                          color: #999;
                        `}
                      >
                        {" "}
                        ({location.specific_location})
                      </span>
                    ) : (
                      ""
                    )}
                  </div>
                ),
              })) ?? []
          }
          value={item.product_location?.id}
          error={getError(item, "product_location")}
          onChange={value => {
            handleUpdateItem(item, { product_location: { id: value } })
          }}
          disabled={item.deleted || !item.product || item.onOrderRequest || isSageLockedLine}
        />
      </div>
      <div>
        <Fields.Number2
          value={item.quantity}
          error={getError(item, "quantity")}
          onChange={value => handleUpdateItem(item, { quantity: value })}
          disabled={item.deleted || item.onOrderRequest}
        />
      </div>

      <div>
        <PoPriceField
          value={item.price_pennies}
          error={getError(item, "price_pennies")}
          onChange={value => handleUpdateItem(item, { price_pennies: value })}
          disabled={item.deleted || item.onOrderRequest}
          inPennies
        />
      </div>
      <div>
        {item.product_location ? (
          <ProductLocationStockDisplaySwitcher
            productLocationId={item.product_location.id}
            unStyled
          />
        ) : (
          "-"
        )}
      </div>
      <div
        css={css`
          height: ${inputHeight}px;
          display: flex;
          align-items: flex-end;
        `}
      >
        {item.onOrderRequest && onViewOrderRequestItem && (
          <Button buttonColor="white" onClick={() => onViewOrderRequestItem()} leftIcon={"eye"}>
            On order request
          </Button>
        )}

        <div>
          {!item.onOrderRequest && (
            <IconButton
              buttonColor="white"
              color="black"
              onClick={() => handleUpdateItem(item, { deleted: !item.deleted })}
              icon={item.deleted ? icons.undo : icons.x}
              square
            />
          )}
        </div>
      </div>
      <Grid.Area column={[1, getColumns().length - 1]}>
        <StockAvailableInOtherBranchesPanel product={item.product} />
      </Grid.Area>
      <div></div>
      {!isFortusSubBranch && onAddItemToOrderRequest && !item.onOrderRequest && (
        // We can get the number of columns from the getColumns FN, so can work out the exact
        // number of cols to cover to make a row
        <Grid.Area column={[1, getColumns().length]}>
          <AddItemToOrderRequest
            item={item}
            onSelect={product => handleAddItemToOrderRequest(item, product)}
            addNonMatchingProductToOrderRequest={
              addNonMatchingProductToOrderRequest
                ? () => addNonMatchingProductToOrderRequest(item)
                : undefined
            }
          />
        </Grid.Area>
      )}
    </React.Fragment>
  )
}

/**
 * Stock of a line's product held by other branches, loaded once `display` is set (it's a slow
 * check, so only on request). `loaded` is false until the check for this product finishes.
 */
export function useStockInOtherBranches(product?: IRecord<"products">) {
  const { handleLoadRelatedProductsStock, loadedStockBranches } = useStockedInAnotherBranch()
  const [display, setDisplay] = useState(false)
  // The product the loaded stock belongs to, so nothing shows until the check for this product finishes
  const [loadedProductId, setLoadedProductId] = useState<string | null>(null)

  useAsyncEffect(async () => {
    if (!product?.id || !display) {
      return
    }
    setLoadedProductId(null)
    await loadStock(product.id)
    setLoadedProductId(product.id)
  }, [product, display])

  async function loadStock(productId: string) {
    const productWithMaster = await skeem.fetch2<IRecord<"products", "product_master">>({
      first: {
        name: "products",
        attributes: ["product_master"],
        filter: { matches: { id: productId } },
      },
    })
    await handleLoadRelatedProductsStock({ product: productWithMaster })
  }

  return {
    display,
    setDisplay,
    loaded: !!product?.id && loadedProductId === product.id,
    branches: loadedStockBranches,
  }
}

export function StockAvailableInOtherBranchesPanel({ product }: { product?: IRecord<"products"> }) {
  const {
    display,
    setDisplay,
    loaded,
    branches: loadedStockBranches,
  } = useStockInOtherBranches(product)

  if (!display && product?.id) {
    return (
      <div
        css={css`
          margin-top: 8px;
        `}
      >
        <Button buttonColor="white" onClick={() => setDisplay(true)} leftIcon={icons.stock}>
          Check stock in other branches
        </Button>
      </div>
    )
  }

  if (!display || !product?.id || !loaded) {
    return null
  }

  if (!loadedStockBranches || loadedStockBranches.length === 0) {
    return (
      <P
        css={css`
          margin-top: 8px;
        `}
      >
        No stock in other branches
      </P>
    )
  }

  return (
    <div
      css={css`
        margin-top: 8px;
      `}
    >
      <InfoBanner
        message={
          <span
            css={css`
              display: block;
              white-space: normal;
              overflow-wrap: anywhere;
              word-break: break-word;
            `}
          >
            {`This product is also available from these branches: ${loadedStockBranches
              .sort((a, b) => (b.quantity ?? 0) - (a.quantity ?? 0))
              .map(v => `${v.name} (${v.quantity})`)
              .join(", ")}`}
          </span>
        }
      />
    </div>
  )
}

type IForeignProduct = {
  product: IRecord<"products", "body" | "stock_code" | "cost_price_pennies">
  stock: IProductStock
}

interface IAddItemToOrderRequestProps {
  item: IPurchaseOrdersForm2Item
  onSelect: (product: IProduct) => void
  addNonMatchingProductToOrderRequest?: () => void
}

/**
 * Checks to see if the item is available in another branch, if it is
 * then a button will be rendered triggering a callback. If not it will render null
 */

export type IProduct = IRecord<
  "products",
  "stock_code" | "body" | "preferred_supplier_list_price_pennies" | "sage_id"
> & {
  suppliers: IRecord<"suppliers", "name">[]
  product_group: IRecord<"product_groups", "preventOrderRequest">[]
}

export type IStockDataWithProduct = IProductStock & { product: IProduct }

/**
 * Whether the default order request branch can supply a line's product, and which of its
 * products match. Shared by the classic and new design forms.
 */
export function useOrderRequestAvailability(item: IPurchaseOrdersForm2Item) {
  const skeem = window.skeem
  const api = useApi()
  const globalState = useGlobalState()
  const { data: defaultOrderRequestBranch } = useOrderRequestDefaultSettings()
  const { dpdProductPrefixes } = useSettings()

  const [productNotExported, setProductNotExported] = useState(false)
  const [noMatchingProducts, setNoMatchingProducts] = useState(false)
  const [stockData, setStockData] = useState<IStockDataWithProduct[] | null>(null)
  const [stockDataWithTooFewStock, setStockDataWithTooFewStock] = useState<
    IStockDataWithProduct[] | null
  >(null)
  const [alternativeProduct, setAlternativeProduct] = useState<IRecord<
    "products",
    "stock_code"
  > | null>(null)

  const [dismissed, setDismissed] = useState(false)

  useAsyncEffect(async () => {
    setDismissed(false)
    setNoMatchingProducts(false)
    if (!defaultOrderRequestBranch) {
      return
    }
    if (!globalState.currentUser.branch?.id) {
      return
    }
    if (!item.product?.id) {
      return
    }
    let relatedProductIds: null | string[] = null
    const primaryProduct = await skeem.fetch2<
      IRecord<"products", "sage_id"> & {
        orderRequestAlternativeProduct: IRecord<"products", "stock_code">
      }
    >({
      first: {
        name: "products",
        attributes: [
          { name: "orderRequestAlternativeProduct", attributes: ["stock_code"] },
          "sage_id",
        ],
        filter: { matches: { id: item.product.id } },
      },
    })
    setProductNotExported(!isProductExported(primaryProduct))
    if (primaryProduct.orderRequestAlternativeProduct) {
      relatedProductIds = [primaryProduct.orderRequestAlternativeProduct.id]
      setAlternativeProduct(primaryProduct.orderRequestAlternativeProduct)
    } else {
      setAlternativeProduct(null)
      relatedProductIds = await findRelatedProductsForBranch(skeem, {
        productId: item.product.id,
        sourceBranchId: globalState.currentUser.branch.id,
        targetBranchId: defaultOrderRequestBranch.id,
      })
    }

    if (relatedProductIds === null) {
      return
    }
    if (relatedProductIds.length === 0) {
      setNoMatchingProducts(true)
      return
    }

    const defaultOrderRequestBranchRecord = await skeem.fetch2<{ settings: IBranchSettings }>({
      first: {
        name: "branches",
        attributes: ["settings"],
        filter: { matches: { id: defaultOrderRequestBranch.id } },
      },
    })

    const stockDataResponse = await api.productsStock(relatedProductIds, {
      warehouseIds: defaultOrderRequestBranchRecord?.settings.stockWarehouses?.map(w => w.id),
    })

    const stockData = Object.values(stockDataResponse)

    const products = await skeem.fetch2<IProduct[]>({
      model: {
        name: "products",
        attributes: [
          "stock_code",
          "body",
          "cost_price_pennies",
          "preferred_supplier_list_price_pennies",
          "sage_id",
          { name: "suppliers", attributes: ["name"] },
          { name: "product_group", attributes: ["preventOrderRequest"] },
        ],
        filter: {
          matches: { id: { or: stockData.map(sd => sd.id) } },
        },
      },
    })

    const stockDataWithProductsWithTooFewStock: IStockDataWithProduct[] = []

    const stockDataWithProducts = stockData
      .map(sd => {
        const product = products.find(product => product.id === sd.id)

        if (!product) {
          return null
        }

        const stockDataWithProduct = { ...sd, product }

        const matchedOrAlt =
          primaryProduct.orderRequestAlternativeProduct &&
          sd.id === primaryProduct.orderRequestAlternativeProduct.id
        if (
          0 >= sd.availableInStock &&
          sd.stock_code?.toUpperCase() !== item.product?.stock_code?.toUpperCase() &&
          !matchedOrAlt
        ) {
          stockDataWithProductsWithTooFewStock.push(stockDataWithProduct)
          return null
        } else {
          return stockDataWithProduct
        }
      })
      .filter(Boolean)

    // We ensure that if there is an exact match, then it is at the top of the list
    const exactMatch = stockDataWithProducts.find(
      sd => sd.product.stock_code === item.product?.stock_code,
    )
    const otherMatches = stockDataWithProducts.filter(sd => sd !== exactMatch)

    setStockData([exactMatch, ...otherMatches].filter(Boolean))
    setStockDataWithTooFewStock(stockDataWithProductsWithTooFewStock)
  }, [item.product, defaultOrderRequestBranch?.id, dpdProductPrefixes])

  return {
    defaultOrderRequestBranch,
    productNotExported,
    noMatchingProducts,
    stockData,
    stockDataWithTooFewStock,
    alternativeProduct,
    dismissed,
    setDismissed,
  }
}

function AddItemToOrderRequest({
  item,
  onSelect,
  addNonMatchingProductToOrderRequest,
}: IAddItemToOrderRequestProps) {
  const {
    defaultOrderRequestBranch,
    productNotExported,
    noMatchingProducts,
    stockData,
    stockDataWithTooFewStock,
    alternativeProduct,
    dismissed,
    setDismissed,
  } = useOrderRequestAvailability(item)
  const [viewStockDataWithTooFewStock, setViewStockDataWithTooFewStock] = useState(false)

  if (noMatchingProducts) {
    return (
      <FloatingBox
        color="#f5f5f5"
        header={
          <TextGroupSmall
            heading={`Request this product from ${defaultOrderRequestBranch?.name}`}
            body={
              dismissed
                ? undefined
                : `This product is not listed in the ${defaultOrderRequestBranch?.name} but may still be available.`
            }
          />
        }
        css={css`
          margin-top: 8px;
        `}
      >
        <QuickToolTipWrapper
          showToolTip={productNotExported && !!item.product}
          toolTipProps={{ interactive: true }}
          content={
            <React.Fragment>
              <P color="white">This product must be exported to be used in an order request.</P>
            </React.Fragment>
          }
        >
          <Button
            onClick={addNonMatchingProductToOrderRequest}
            extendTheme={{ padding: 6 }}
            disabled={productNotExported}
          >
            Request this product
          </Button>
        </QuickToolTipWrapper>
      </FloatingBox>
    )
  }

  if (!stockData) {
    return null
  }

  if (stockData.length === 0) {
    return null
  }

  return (
    <FloatingBox
      color="#f5f5f5"
      header={
        <Grid columns={["auto", "max-content"]} alignItems="center">
          <TextGroupSmall
            heading={`${defaultOrderRequestBranch?.name} has this item available to order`}
            body={dismissed ? undefined : "Click the button below to make an order request"}
          />
          <Button
            buttonColor="white"
            onClick={() => {
              setDismissed(!dismissed)
            }}
            extendTheme={{ padding: 6 }}
          >
            <Icon icon={dismissed ? "eye" : "eye-slash"} /> {dismissed ? "Show" : "Hide"}
          </Button>
        </Grid>
      }
      css={css`
        margin-top: 8px;
        ${FloatingBox.vars.childPadding}: 0;

        table {
          background-color: white;
        }
      `}
    >
      {!dismissed && (
        <React.Fragment>
          <div
            css={css`
              padding: 16px;
              background-color: white;
            `}
          >
            <StockDataTable
              item={item}
              onSelect={onSelect}
              stockData={stockData}
              alternativeProduct={alternativeProduct}
              productNotExported={productNotExported}
            />
            {stockDataWithTooFewStock && stockDataWithTooFewStock.length > 0 && (
              <div>
                <Button onClick={() => setViewStockDataWithTooFewStock(true)} buttonStyle="text">
                  + {stockDataWithTooFewStock.length} product
                  {stockDataWithTooFewStock.length === 1 ? "" : "s"} with no stock
                </Button>
              </div>
            )}
          </div>
          <Modal
            onRequestClose={() => setViewStockDataWithTooFewStock(false)}
            width={650}
            css={css`
              max-width: calc(100% - 16px);
              overflow: initial;
            `}
            render={() => (
              <React.Fragment>
                <TextGroupMedium
                  heading={"Matching products with not enough stock"}
                  theme={{ heading: { size: "small", blockSpacing: 0.5 } }}
                />
                <Spacer size={24} />

                <React.Fragment>
                  <StockDataTable
                    onSelect={onSelect}
                    item={item}
                    stockData={stockDataWithTooFewStock || []}
                    productNotExported={productNotExported}
                  />
                  <Spacer />
                  <div>
                    <Button
                      children="Close"
                      onClick={() => setViewStockDataWithTooFewStock(false)}
                      buttonColor="white"
                    />
                  </div>
                </React.Fragment>
              </React.Fragment>
            )}
            isOpen={viewStockDataWithTooFewStock}
          />
        </React.Fragment>
      )}
    </FloatingBox>
  )
}

function StockDataTable({
  item,
  onSelect,
  stockData,
  alternativeProduct,
  productNotExported,
}: {
  item: IAddItemToOrderRequestProps["item"]
  onSelect?: (product: IProduct) => void
  stockData: IStockDataWithProduct[]
  alternativeProduct?: IRecord<"products", "stock_code"> | null
  productNotExported: boolean
}) {
  const { currentUser } = useGlobalState()
  return (
    <Table data={stockData}>
      <Table.Column heading="Stock Code">
        {row => {
          const stockCode = row.stock_code
          console.log(row, alternativeProduct)
          return (
            <div
              css={css`
                ${stockCode === item.product?.stock_code &&
                css`
                  font-weight: bold;
                `}
              `}
            >
              {stockCode}
              {stockCode === item.product?.stock_code && (
                <div
                  css={css`
                    color: grey;
                  `}
                >
                  (exact match)
                </div>
              )}
              {alternativeProduct && row.product.id === alternativeProduct.id && (
                <div>
                  <QuickToolTip
                    content={
                      <P color="white">
                        This product has been marked as the alternative product for{" "}
                        {item.product?.stock_code}.
                      </P>
                    }
                  >
                    <div
                      css={css`
                        color: grey;
                        border-bottom: 1px dotted grey;
                      `}
                    >
                      (alternative product)
                    </div>
                  </QuickToolTip>
                </div>
              )}
            </div>
          )
        }}
      </Table.Column>
      <Table.Column children={row => row.product.body} heading="Body" />
      <Table.Column
        children={row => row.product.suppliers.map((supplier: any) => supplier.name).join(", ")}
        heading="Suppliers"
      />
      <Table.Column heading="Quantity Available">
        {row => (
          <div>
            <b>{row.availableInStock}</b>
            {row.onOrderFromSage.length > 0 ? (
              <div>
                Currently on order:{" "}
                {row.onOrderFromSage.map(
                  (order: IStockDataWithProduct["onOrderFromSage"][0], i: number) => (
                    <div key={i}>
                      {order.quantity}
                      {order.requested_delivery_date
                        ? ` expected ${formatDate(new Date(order.requested_delivery_date))}`
                        : " (no est date)"}
                    </div>
                  ),
                )}
              </div>
            ) : row.availableInStock < (item.quantity ?? 1) ? (
              <div
                css={css`
                  color: #999;
                `}
              >
                None currently on order
              </div>
            ) : (
              ""
            )}
          </div>
        )}
      </Table.Column>
      <Table.Column
        children={row => (
          <QuickToolTipWrapper
            showToolTip={!!(productNotExported && !!row.product)}
            toolTipProps={{ interactive: true }}
            content={
              <React.Fragment>
                <P color="white">
                  The product from your branch must be exported to be used in an order request.
                </P>
              </React.Fragment>
            }
          >
            <ConditionalWrapper
              condition={row.product.product_group?.preventOrderRequest}
              Wrapper={QuickToolTip}
              wrapperProps={{ content: "Order requests have been disabled for this product group" }}
            >
              <Button
                onClick={() => onSelect?.(row.product)}
                extendTheme={{ padding: 6 }}
                disabled={row.product.product_group?.preventOrderRequest || productNotExported}
              >
                Request this product
              </Button>
            </ConditionalWrapper>
          </QuickToolTipWrapper>
        )}
      />
    </Table>
  )
}

async function findRelatedProductsForBranch(
  skeem: IWebClient,
  opts: {
    productId: string
    sourceBranchId: string
    targetBranchId: string
  },
) {
  type IProduct = IRecord<"products", "stock_code" | "legacy_stock_code">

  const branch = await skeem.fetch2<{ settings: IBranchSettings }>({
    first: { name: "branches", filter: { matches: { id: opts.targetBranchId } } },
  })
  const dpdProductFilter = productDpdFilter(branch.settings?.dpdProductPrefixes ?? "")

  const product = await skeem.fetch2<IProduct>({
    first: {
      name: "products",
      attributes: ["stock_code", "legacy_stock_code"],
      filter: {
        matches: {
          branch: opts.sourceBranchId,
          id: opts.productId,
        },
      },
    },
  })

  if (!product) {
    console.error(`Could not find product with id ${opts.productId}`)
    return []
  }

  const relatedProducts = await skeem.fetch2<IRecord<"products">[]>({
    model: {
      name: "products",
      filter: {
        and: [
          { matches: { branch: opts.targetBranchId } },
          {
            or: [
              {
                eq: [
                  { trim: { uppercase: { attr: "stock_code" } } },
                  { value: product.stock_code.toUpperCase() },
                ],
              },
              { matches: { stock_code: product.stock_code } },
              { matches: { stock_code: product.legacy_stock_code } },
            ],
          },
          dpdProductFilter.and.length > 0 && dpdProductFilter,
        ].filter(Boolean),
      },
    },
  })

  return relatedProducts.map(product => product.id)
}

export type IShortFallProduct = IRecord<"products", "stock_code" | "body"> & {
  product_locations: IRecord<"product_locations", "warehouse">[]
  stockData: IProductLocationStockResponseItem & { shortfall: number }
}
type IShortFallSupplier = IRecord<"suppliers", "name"> & {
  branch: IRecord<"branches"> & { settings: IBranchSettings }
  products: IShortFallProduct[]
}

/**
 * The supplier's products whose stock (in stock + on purchase order - allocated) is below zero,
 * leaving out those already on the order. Shared by the classic and new design forms.
 */
export function useSupplierShortfall(supplierId?: string | null, filterProductIds?: string[]) {
  const api = useApi()
  const skeem = window.skeem
  const [supplier, setSupplier] = useState<IShortFallSupplier | null>(null)

  async function loadSupplier(supplierId: string) {
    const supplier = await skeem.fetch2<IShortFallSupplier>({
      first: {
        name: "suppliers",
        attributes: [
          "name",
          { name: "branch", attributes: ["settings"] },
          {
            name: "products",
            attributes: [
              "stock_code",
              "body",
              { name: "product_locations", attributes: ["warehouse"] },
            ],
          },
        ],
        filter: { matches: { id: supplierId } },
      },
    })
    const warehouses = supplier.branch.settings.stockWarehouses?.map(w => w.id)
    const productLocationIds = supplier.products.flatMap(p =>
      p.product_locations
        .filter(
          x => x.warehouse?.id && (warehouses.length === 0 || warehouses.includes(x.warehouse?.id)),
        )
        .map(x => x.id),
    )
    let stockData: Record<string, IProductLocationStockResponseItem> = {}
    if (productLocationIds.length > 0) {
      stockData = await api.productLocationsStock(productLocationIds)
    }

    supplier.products.forEach(product => {
      const combinedData = combineProductLocationStockData(
        product.product_locations.map(p => stockData[p.id]).filter(Boolean),
      ) as IProductLocationStockResponseItem & { shortfall: number }
      const shortfall =
        combinedData.inStock + combinedData.onPurchaseOrderStock - combinedData.allocatedStock
      combinedData.shortfall = Math.min(shortfall, 0)
      product.stockData = combinedData
    })
    setSupplier(supplier)
  }

  useAsyncEffect(async () => {
    if (!supplierId) {
      return
    }
    await loadSupplier(supplierId)
  }, [supplierId])

  const shortFallItems =
    !supplierId || !supplier
      ? []
      : supplier.products.filter(
          x =>
            (filterProductIds ? !filterProductIds.includes(x.id) : true) &&
            x.stockData.shortfall < 0,
        )

  return { supplier, shortFallItems }
}

function SupplierShortfallList({
  supplierId,
  onAddProduct,
  filterProductIds,
}: {
  supplierId?: string | null
  onAddProduct: (productId: string, qty: number) => Promise<void>
  filterProductIds?: string[]
}) {
  const [isOpen, setIsOpen] = useState(false)
  const { supplier, shortFallItems } = useSupplierShortfall(supplierId, filterProductIds)

  if (!supplierId || !supplier || shortFallItems.length === 0) {
    return null
  }

  const header = [
    pluralize(shortFallItems.length, "item", "items"),
    "from",
    supplier.name,
    "with shortfall",
  ].join(" ")

  function AddProductButton({ item }: { item: IShortFallProduct }) {
    const handleAddProduct = useAsyncCallback(async () => {
      await onAddProduct(item.id, (item.stockData?.shortfall ?? 0) * -1)
    })
    return (
      <div
        css={css`
          padding-right: 8px;
        `}
      >
        <Button
          rightIcon={icons.plus}
          onClick={handleAddProduct}
          disabled={handleAddProduct.running}
        >
          Add
        </Button>
      </div>
    )
  }

  return (
    <React.Fragment>
      <FloatingBox
        header={
          <Grid columns={["auto", "max-content"]} gap={16} alignItems="center">
            <H4>{header}</H4>
            <IconButton
              icon={isOpen ? icons.chevronUp : icons.chevronDown}
              onClick={() => setIsOpen(!isOpen)}
              padding={4}
              iconSize={12}
              buttonStyle="text"
              square
            />
          </Grid>
        }
      >
        {isOpen ? (
          <div
            css={css`
              max-height: 400px;
              overflow: auto;
            `}
          >
            <TextBlock blockSpacing={0}>
              <Grid
                columns={[{ fr: 1 }, "max-content", "max-content"]}
                alignItems="center"
                gap={{ columns: 32, rows: 8 }}
              >
                <React.Fragment>
                  <div
                    css={css`
                      position: sticky;
                      top: 0;
                      background-color: white;
                    `}
                  >
                    <P weight={600}>Product</P>
                  </div>
                  <div
                    css={css`
                      position: sticky;
                      top: 0;
                      background-color: white;
                    `}
                  >
                    <P weight={600}>Shortfall</P>
                  </div>
                  <div
                    css={css`
                      position: sticky;
                      top: 0;
                      background-color: white;
                      height: 100%;
                      width: 100%;
                    `}
                  ></div>
                </React.Fragment>
                {shortFallItems.map(item => (
                  <React.Fragment key={item.id}>
                    <div>
                      <P>{item.stock_code}</P>
                      <P size="small">{item.body}</P>
                    </div>
                    <div>
                      <P>{item.stockData.shortfall * -1}</P>
                    </div>
                    <AddProductButton item={item} />
                  </React.Fragment>
                ))}
              </Grid>
            </TextBlock>
            <Spacer />
          </div>
        ) : null}
      </FloatingBox>
      <Spacer />
    </React.Fragment>
  )
}
